import ChalkySticks from '@chalkysticks/sdk';
import State from './State';
import {
	getActiveBallSets,
	getBallSetById,
	getBallSetPaths,
	getBallSetsByGameType,
	type IBallSetConfig,
} from '@/Asset/data/ball-sets';

/**
 * Getters are used to calculate values based off state. They are generally
 * used for filtering or formatting data.
 *
 * @class Getters
 * @package Store/Module/Global
 * @project ChalkySticks Pad
 */
const getters = {
	/**
	 * Get available ball set based on current configuration
	 *
	 * @param State state
	 * @return Record<string, string>
	 */
	availableBallSet(state: typeof State): Record<string, string> {
		// 'cyclop-pool'
		// 'chalkysticks-pool'
		// 'generic-pool'
		// 'snooker-standard'
		// 'billiards-standard'
		const ballSetId = 'chalkysticks-pool';

		return getBallSetPaths(ballSetId);
	},

	/**
	 * Get all available ball sets
	 *
	 * @param State state
	 * @return IBallSetConfig[]
	 */
	availableBallSets(state: typeof State): IBallSetConfig[] {
		return getActiveBallSets();
	},

	/**
	 * Get ball sets filtered by game type
	 *
	 * @param State state
	 * @return function
	 */
	ballSetsByGameType(state: typeof State) {
		return (gameType: 'pool' | 'snooker' | 'billiards' | 'carom'): IBallSetConfig[] => {
			return getBallSetsByGameType(gameType);
		};
	},

	/**
	 * @param State state
	 * @return boolean
	 */
	canDragBall(state: typeof State): boolean {
		return getters.isBallMode(state) || getters.isNoneMode(state);
	},

	/**
	 * Get current ball set configuration
	 *
	 * @param State state
	 * @return IBallSetConfig | null
	 */
	currentBallSet(state: typeof State): IBallSetConfig | null {
		// For now, default to cyclop-pool set
		// TODO: Make this configurable based on user preferences or game type
		const ballSetId = 'cyclop-pool';

		return getBallSetById(ballSetId);
	},

	/**
	 * Get diagram state
	 *
	 * @param State state
	 * @return ChalkySticks.Pad.Enum.DiagramState
	 */
	diagramState(state: typeof State): ChalkySticks.Pad.Enum.DiagramState {
		return state.diagramState;
	},

	/**
	 * @param State state
	 * @return boolean
	 */
	hasCompletedTutorial(state: typeof State): boolean {
		return state.completedTutorial;
	},

	/**
	 * @param State state
	 * @return boolean
	 */
	isBallMode(state: typeof State): boolean {
		return state.diagramState === ChalkySticks.Pad.Enum.DiagramState.BallMode;
	},

	/**
	 * @param State state
	 * @return boolean
	 */
	isBallSelectionMode(state: typeof State): boolean {
		return state.diagramState === ChalkySticks.Pad.Enum.DiagramState.BallSelect;
	},

	/**
	 * @param State state
	 * @return boolean
	 */
	isLineMode(state: typeof State): boolean {
		return state.diagramState === ChalkySticks.Pad.Enum.DiagramState.LineMode;
	},

	/**
	 * @param State state
	 * @return boolean
	 */
	isNoneMode(state: typeof State): boolean {
		return state.diagramState === ChalkySticks.Pad.Enum.DiagramState.None;
	},

	/**
	 * @param State state
	 * @return boolean
	 */
	showBallTray(state: typeof State): boolean {
		return getters.isBallMode(state) || getters.isBallSelectionMode(state);
	},
};

export default getters;
